import React, { useState, useEffect, useCallback } from 'react';
import BrokerNotice from './common/BrokerNotice';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { Percent, ChevronDown, ChevronUp, CreditCard, AlertTriangle } from 'lucide-react';

/**
 * 매칭 수수료 내역 (/commissions, 기공소용)
 * - 월별 청구서: 기준액(전월 확정 명세서 합계) × 4.5% + VAT
 * - 근거 명세서 내역(breakdown) 투명 공개
 * - 실패/연체 청구서 수동 결제
 */
const STATUS_LABEL = {
    pending: { text: '결제 대기', color: '#D97706', bg: '#FFFBEB' },
    paid: { text: '결제 완료', color: '#059669', bg: '#ECFDF5' },
    failed: { text: '결제 실패', color: '#DC2626', bg: '#FEF2F2' },
    overdue: { text: '연체', color: '#fff', bg: '#DC2626' },
    waived: { text: '청구 없음', color: '#6B7280', bg: '#F3F4F6' },
};

const fmt = (n) => (n || 0).toLocaleString('ko-KR');

function CommissionList({ user }) {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [paying, setPaying] = useState(null);
    const [clinicNames, setClinicNames] = useState({});

    const fetchInvoices = useCallback(async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        try {
            const ids = [uid];
            if (user?.companyId && user.companyId !== uid) ids.push(user.companyId);

            const results = [];
            for (const labId of ids) {
                const snap = await getDocs(query(collection(db, 'commissionInvoices'), where('labId', '==', labId)));
                snap.forEach(d => results.push({ id: d.id, ...d.data() }));
            }
            results.sort((a, b) => (b.month || '').localeCompare(a.month || ''));
            setInvoices(results);

            // breakdown의 치과 이름 로드
            const clinicIds = [...new Set(results.flatMap(inv => (inv.breakdown || []).map(b => b.clinicId)))];
            const names = {};
            await Promise.all(clinicIds.map(async cid => {
                try {
                    const { doc, getDoc } = await import('firebase/firestore');
                    const u = await getDoc(doc(db, 'users', cid));
                    names[cid] = u.exists() ? (u.data().businessName || u.data().name || cid) : cid;
                } catch (_) { names[cid] = cid; }
            }));
            setClinicNames(names);
        } catch (e) {
            console.error('수수료 내역 조회 실패:', e);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    const handlePay = async (invoiceId) => {
        if (!window.confirm('이 청구서를 등록된 카드로 결제할까요?')) return;
        setPaying(invoiceId);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'payCommissionInvoice');
            await fn({ invoiceId });
            alert('결제가 완료되었습니다.');
            await fetchInvoices();
        } catch (err) {
            const msg = err.message || '';
            if (msg.includes('NEED_BILLING_KEY')) {
                if (window.confirm('등록된 결제 카드가 없습니다. 카드 등록 화면으로 이동할까요?')) navigate('/subscription');
            } else {
                alert(msg || '결제에 실패했습니다.');
            }
        } finally {
            setPaying(null);
        }
    };

    const hasOverdue = invoices.some(i => i.status === 'overdue');

    return (
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
            <BrokerNotice style={{ margin: '0 0 12px' }} />
            {/* 📢 수수료 전면 폐지 안내 (약관 v6.0, 2026-08-17) — 아래 목록은 과거 기록 열람용으로 유지 */}
            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#065F46', lineHeight: 1.7 }}>
                <b>2026-08-17부로 중개 수수료가 전면 폐지되었습니다.</b> (테스트보철 건당 이용료·매칭 수수료·해외 중개 포함, 약관 v6.0 제4조의5)<br />
                아래는 과거 기록 열람용입니다. 새로운 수수료는 청구되지 않습니다.
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
                <Percent size={24} color="#2563EB" /> 매칭 수수료
            </h1>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20, lineHeight: 1.7 }}>
                과거 청구분(폐지 전 기준: 전월 확정 거래명세서 합계 × 수수료 4.5% + 부가세)의 내역을 열람하는 화면입니다.
            </p>

            {hasOverdue && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FEF2F2', color: '#DC2626', padding: '12px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                    <AlertTriangle size={18} /> 연체된 수수료가 있습니다. 결제 전까지 신규 테스트보철 수락이 제한됩니다.
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>불러오는 중...</div>
            ) : invoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12 }}>
                    아직 수수료 청구 내역이 없습니다.<br />
                    <span style={{ fontSize: 13 }}>매칭 거래의 명세서가 확정된 다음 달 10일부터 청구서가 생성됩니다.</span>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {invoices.map(inv => {
                        const s = STATUS_LABEL[inv.status] || STATUS_LABEL.pending;
                        const isOpen = expanded === inv.id;
                        const payable = ['pending', 'failed', 'overdue'].includes(inv.status);
                        return (
                            <div key={inv.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: 16 }}>
                                    <div style={{ fontWeight: 700, fontSize: 16, minWidth: 80 }}>{inv.month}</div>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '4px 10px', borderRadius: 12 }}>{s.text}</span>
                                    <div style={{ flex: 1 }} />
                                    <div style={{ textAlign: 'right', fontSize: 13, color: '#6B7280' }}>
                                        <div>기준액 {fmt(inv.baseAmount)}원</div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                                            청구 {fmt(inv.totalAmount)}원
                                            <span style={{ fontSize: 12, fontWeight: 400, color: '#9CA3AF' }}> (수수료 {fmt(inv.commissionAmount)} + VAT {fmt(inv.vat)})</span>
                                        </div>
                                    </div>
                                    {payable && (
                                        <button onClick={() => handlePay(inv.id)} disabled={paying === inv.id}
                                            style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563EB', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                            <CreditCard size={15} /> {paying === inv.id ? '결제 중...' : '지금 결제'}
                                        </button>
                                    )}
                                    {(inv.breakdown || []).length > 0 && (
                                        <button onClick={() => setExpanded(isOpen ? null : inv.id)}
                                            style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 4 }}>
                                            {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                        </button>
                                    )}
                                </div>
                                {isOpen && (
                                    <div style={{ borderTop: '1px solid #F3F4F6', padding: '12px 16px', background: '#F9FAFB' }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>청구 근거 (전월 확정 거래명세서)</div>
                                        {(inv.breakdown || []).map((b, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                                                <span>
                                                    {clinicNames[b.clinicId] || b.clinicId} — 명세서 {b.statementCount}건
                                                    {b.source === 'emergency_outsource' && (
                                                        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#4338CA', background: '#EEF2FF', borderRadius: 4, padding: '1px 6px' }}>
                                                            긴급외주
                                                        </span>
                                                    )}
                                                </span>
                                                <span style={{ fontWeight: 600 }}>{fmt(b.subtotal)}원</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default CommissionList;
